/**
 * svgPolicy — 서버 SVG 정제기(`services/svg_sanitize.py`, SANITIZER_VERSION 1)의 **클라이언트 거울**.
 *
 * 편집기가 마운트 전·저장 전에 거른다(계약 BE-20261015 §3-3 · §6-3). 허용목록·거절 code·경고 문자열은 서버와
 * 같은 이름이고 **같은 입력에 같은 결과**를 내는 것이 목표다. 서버와 다른 점 하나 — 문자열이 아니라 **고친 DOM
 * (분리된 DOMParser 문서의 루트)** 을 돌려준다. 편집기는 그 root 를 `document.importNode` 로 마운트하고,
 * `serializeSvg(root)` 로 다시 문자열을 만든다.
 *
 * 거절(ok:false)은 통과시킬 수 없는 것 — 실행 가능한 것(script·on*·href·외부 참조·url()/src()/image-set()/var()
 * 값), 파서 공격(DTD·entity·처리 명령·홀로 선 서로게이트), 참조형 속성(marker 단축·mask·filter), 그리고 「고치면
 * 의미가 바뀌는 것」(모르는 요소·끊긴 참조·엉뚱한 자리의 글자). 경고(warnings)는 의미를 바꾸지 않는 손질 — 메타
 * 제거·흰 배경 제거·모르는 속성 제거·검정→currentColor·글꼴 generic 화·루트 width/height 의 px 제거 — 로,
 * 통과시키되 무엇을 손댔는지 남긴다(같은 경고는 한 번만).
 *
 * 서버 파서(ElementTree)와 브라우저 DOM 의 차이는 여기서 흡수한다: 주석은 서버가 못 보므로 조용히 지우고,
 * 네임스페이스 선언(xmlns·xmlns:*)도 서버가 속성으로 안 보므로 조용히 지운다(루트의 `xmlns="SVG NS"` 만 남김).
 * Python `\w` 는 유니코드 글자·숫자를 받으므로 JS 쪽은 `[\p{L}\p{N}_]` 로 맞췄다.
 */
import { utf8ByteLength } from './figureSvg';

export const SANITIZER_VERSION = 1;
export const SVG_NS = 'http://www.w3.org/2000/svg';
const XML_NS = 'http://www.w3.org/XML/1998/namespace';
const XMLNS_NS = 'http://www.w3.org/2000/xmlns/';

export const MAX_BYTES = 500000;
export const MAX_ELEMENTS = 5000;
export const MAX_DEPTH = 32;

const words = (s) => s.trim().split(/\s+/);

/** 표현 속성(서버 _PRESENTATION — transform 포함). <style>·style= 선언으로 와도 속성으로 평탄화된다. */
export const PRESENTATION_ATTRS = new Set(words(`fill fill-rule fill-opacity stroke stroke-width stroke-linecap stroke-linejoin
  stroke-miterlimit stroke-dasharray stroke-dashoffset stroke-opacity opacity font-family font-size
  font-weight font-style text-anchor dominant-baseline letter-spacing text-decoration paint-order
  vector-effect clip-path clip-rule marker-start marker-mid marker-end
  baseline-shift alignment-baseline transform-origin pathLength display visibility transform`));
/** 기하 속성(서버 _GEOMETRY) — style 선언으로 오면 거절(기하가 조용히 바뀌는 것 방지). */
export const GEOMETRY_ATTRS = new Set(words('x y width height d points cx cy r rx ry x1 y1 x2 y2 dx dy'));
/** 모든 요소에 허용되는 속성(서버 _ATTRS). */
const ELEMENT_ATTRS = new Set([...PRESENTATION_ATTRS, ...words(`id class width height viewBox preserveAspectRatio transform
  d points x y x1 y1 x2 y2 cx cy r rx ry dx dy rotate textLength lengthAdjust
  markerWidth markerHeight refX refY orient markerUnits clipPathUnits gradientUnits gradientTransform
  spreadMethod fx fy offset stop-color stop-opacity`)]);
/** 루트 전용 — 서버가 찍는 정체성. keepStamp=false 면 제거 + 경고. */
export const ROOT_ONLY_ATTRS = new Set(['data-figure', 'data-revision']);
/** 허용 속성 전체(루트 전용·xmlns 포함). xmlns 는 루트에서 값이 SVG NS 일 때만. */
export const ALLOWED_ATTRS = new Set([...ELEMENT_ATTRS, ...ROOT_ONLY_ATTRS, 'xmlns']);
/** 외부·참조형 — 속성이든 style 선언이든 거절(+ on* 전부). */
export const FORBIDDEN_ATTRS = new Set(['href', 'marker', 'mask', 'filter']);

/** 저장본에 남을 수 있는 요소. 루트 <svg> 는 루트만, <style> 은 입력에서만 받고 평탄화 뒤 제거, title/desc/metadata 는 제거. */
export const ALLOWED_ELEMENTS = new Set(words(`g rect path line ellipse circle polyline polygon text tspan defs clipPath marker
  linearGradient radialGradient stop`));
const INPUT_ELEMENTS = new Set([...ALLOWED_ELEMENTS, 'style']);
const META = new Set(['title', 'desc', 'metadata']);
const DEF_KINDS = new Set(['marker', 'clipPath', 'linearGradient', 'radialGradient']);
/** url(#id) 를 허용하는 속성 → 가리킬 수 있는 요소 종류. */
export const REF_KINDS = Object.freeze({
  fill: ['linearGradient', 'radialGradient'],
  stroke: ['linearGradient', 'radialGradient'],
  'marker-start': ['marker'],
  'marker-mid': ['marker'],
  'marker-end': ['marker'],
  'clip-path': ['clipPath'],
});
const COLOR_ATTRS = new Set(['fill', 'stroke', 'stop-color']);
// 아래 두 줄은 UI 색이 아니라 서버 정제기 _BLACK/_WHITE 와 같은 **판정 기준값**(검정→currentColor 치환, 흰 배경 rect 판정)이다.
// eslint-disable-next-line no-restricted-syntax
const BLACK = new Set(['#000', '#000000', 'black', 'rgb(0,0,0)']);
// eslint-disable-next-line no-restricted-syntax
const WHITE = new Set(['white', '#fff', '#ffffff', 'rgb(255,255,255)']);
const GENERIC_FONTS = new Set(['serif', 'sans-serif', 'monospace']);

const XML_DECL = /^\s*<\?xml\b[^>]*\?>/;
const FORBIDDEN_DECL = /<!DOCTYPE|<!ENTITY|<\?/i;
/** 서버 _BAD_VALUE 와 같은 패턴. 제어 문자를 뗀 값(probe)에 대소문자 무시로 건다. */
export const BAD_VALUE = /javascript\s*:|data\s*:|expression\s*\(|var\s*\(|@import|behavior|binding|[<>\\]/i;
export const URL_FUNC = /(?:url|src|image-set)\s*\(/i;
export const URL_REF = /^url\(\s*#([A-Za-z_][\p{L}\p{N}_.-]*)\s*\)$/u;
// 제어 문자로 쪼갠 'java\nscript:' 도 잡는다(서버 _CONTROL). 저장 값은 그대로 두고 검사용 probe 에서만 뗀다.
// eslint-disable-next-line no-control-regex
const CONTROL = /[\x00-\x1f]/g;
const NUMBER = /^[+-]?([0-9]+\.?[0-9]*|\.[0-9]+)([eE][+-]?[0-9]+)?$/;   // \d 는 전각 숫자도 받아서 안 씀(서버와 같게)
const DECL_NAME = /^[A-Za-z-]+$/;
const CSS_RULE = /([^{}]+)\{([^{}]*)\}\s*/y;
const CSS_CLASS = /^\.[A-Za-z_][\p{L}\p{N}_-]*$/u;
const SURROUNDING_ASCII_WS = /^[ \t\r\n]+|[ \t\r\n]+$/g;

/** DOMParser 가 파서 오류를 알리는 요소의 네임스페이스 — jsdom·Firefox / Chrome. */
const PARSERERROR_NS = new Set(['http://www.mozilla.org/newlayout/xml/parsererror.xml', 'http://www.w3.org/1999/xhtml']);

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;
const CDATA_NODE = 4;
const COMMENT_NODE = 8;

class Rejected extends Error {
  constructor(code, reason) {
    super(reason);
    this.code = code;
    this.reason = reason;
  }
}

const commas = (n) => n.toLocaleString('en-US');

// ── 공개 API ─────────────────────────────────────────────────────────

/**
 * SVG 문자열 검사·정제.
 *
 * @returns {{ok:true, root:Element, svg:string, warnings:string[], width:number, height:number, elements:number, bytes:number}
 *         | {ok:false, code:string, reason:string}}
 *   root 는 분리된 DOMParser 문서의 루트(정제 반영). svg 는 serializeSvg(root). width/height 는 viewBox 의 폭·높이.
 * @param {object} [options]
 * @param {boolean} [options.keepStamp=true] 루트 data-figure/data-revision 유지. false 면 서버 기본처럼 제거 + 경고.
 *   (서버는 어차피 입력 스탬프를 무시하고 자기 것을 찍는다 — 편집기는 유지해 두는 쪽이 왕복에 편하다.)
 * @param {number} [options.maxBytes] @param {number} [options.maxElements] @param {number} [options.maxDepth]
 */
export function checkSvg(svgText, { keepStamp = true, maxBytes = MAX_BYTES, maxElements = MAX_ELEMENTS, maxDepth = MAX_DEPTH } = {}) {
  try {
    return sanitize(svgText, keepStamp, maxBytes, maxElements, maxDepth);
  } catch (e) {
    if (e instanceof Rejected) return { ok: false, code: e.code, reason: e.reason };
    throw e;
  }
}

/**
 * 루트 요소 → SVG 문자열(XMLSerializer). 접두 없는 루트 태그(`<svg …>`)에 xmlns 기본 네임스페이스 선언을 보장한다.
 * 접두 루트(`<svg:svg …>`)는 손대지 않는다 — checkSvg 가 파싱 단계에서 기본 네임스페이스 <svg> 로 정규화하므로 여기 올 일이 없고,
 * `:svg` 앞에 xmlns 를 끼우면 깨진 XML 이 된다.
 */
export function serializeSvg(root) {
  const out = new XMLSerializer().serializeToString(root);
  const tag = /^<svg(?=[\s/>])[^>]*>/.exec(out);
  if (tag && !/\sxmlns="http:\/\/www\.w3\.org\/2000\/svg"/.test(tag[0])) {
    return out.replace(/^<svg(?=[\s/>])/, `<svg xmlns="${SVG_NS}"`);
  }
  return out;
}

/**
 * DOMParser 가 넣는 파서 오류 요소(없으면 null). jsdom·Firefox 는 Mozilla 네임스페이스의 <parsererror> 를 루트로 세우고, Chrome 은
 * XHTML 네임스페이스의 <parsererror> 를 문서 안에 끼운다. SVG 네임스페이스의 문자 그대로 <parsererror> 는 오류가 아니라 모르는
 * 요소(→ forbidden_element, 서버와 같은 code)라 여기서 걸리지 않는다.
 */
export function parserError(doc) {
  const root = doc.documentElement;
  if (root && root.localName === 'parsererror') return root;
  return Array.from(doc.querySelectorAll('parsererror')).find((el) => PARSERERROR_NS.has(el.namespaceURI)) || null;
}

/** 서버 응답의 sanitizer_version 이 이 모듈과 다르면 콘솔 경고(계약 §6-3). null(픽셀)·같음 → true. */
export function checkSanitizerVersion(serverVersion) {
  if (serverVersion === null || serverVersion === undefined || serverVersion === SANITIZER_VERSION) return true;
  console.warn(`[svgPolicy] 서버 정제기 버전 ${serverVersion} ≠ 클라이언트 ${SANITIZER_VERSION} — 정책 거울이 어긋났을 수 있습니다`);
  return false;
}

// ── 단계별(서버 sanitize 와 같은 순서) ───────────────────────────────

function sanitize(svgText, keepStamp, maxBytes, maxElements, maxDepth) {
  const root = parse(precheck(svgText, maxBytes));
  if (root.localName !== 'svg' || root.namespaceURI !== SVG_NS) throw new Rejected('bad_root', '루트가 <svg> 가 아닙니다');
  const warnings = [];
  const ids = new Map();
  const drops = [];
  if (!keepStamp) {
    for (const name of [...ROOT_ONLY_ATTRS].sort()) {
      if (root.hasAttribute(name)) {
        root.removeAttribute(name);
        warnings.push(`dropped_attr:${name}`);
      }
    }
  }
  walk(root, ids, drops, warnings, maxElements, maxDepth);
  const index = classIndex(root);
  for (const [el, kind] of drops) {
    if (kind === 'style') flattenSheet(index, el);
    else warnings.push('dropped_meta');
    el.remove();
  }
  for (const el of allElements(root)) fixValues(el, ids, warnings);
  const box = rootBox(root, warnings);
  dropBackground(root, box, warnings);
  const svg = serializeSvg(root);
  const bytes = utf8ByteLength(svg);
  if (bytes > maxBytes) throw new Rejected('too_large', `정제 결과가 ${commas(maxBytes)} 바이트를 넘습니다`);
  return {
    ok: true, root, svg, warnings: [...new Set(warnings)], width: box[2], height: box[3], elements: allElements(root).length, bytes,
  };
}

/** 원문 문자열 검사: 크기, BOM·XML 선언 벗기기, DTD·entity·처리 명령 차단. */
function precheck(svgText, maxBytes) {
  if (typeof svgText !== 'string' || !svgText.trim()) {
    throw new Rejected('too_large', `SVG 가 비었거나 ${commas(maxBytes)} 바이트를 넘습니다`);
  }
  let text = svgText.replace(/^﻿+/, '');   // 파일 저장본의 BOM — 선언 앞에 있으면 XML_DECL 이 못 벗긴다
  const size = utf8ByteLength(text);
  if (size < 0) throw new Rejected('malformed', 'SVG 문자열에 올바르지 않은 유니코드 문자가 있습니다');
  if (size > maxBytes) throw new Rejected('too_large', `SVG 가 비었거나 ${commas(maxBytes)} 바이트를 넘습니다`);
  text = text.replace(XML_DECL, '');
  if (FORBIDDEN_DECL.test(text)) throw new Rejected('forbidden_declaration', 'DOCTYPE·ENTITY·처리 명령이 있는 SVG 는 받지 않습니다');
  return text;
}

function parse(text) {
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  const err = parserError(doc);
  if (err) throw new Rejected('malformed', `SVG 문법 오류: ${(err.textContent || '').trim().split('\n')[0].slice(0, 200)}`);
  return unprefixRoot(doc);
}

/**
 * 접두 루트(`<svg:svg xmlns:svg="…">`)를 기본 네임스페이스 `<svg xmlns="…">` 로 — 서버 ET 는 접두를 버리고 기본 네임스페이스로
 * 내므로(register_namespace('')) 같은 문자열이 되게 새 루트를 만들어 속성(네임스페이스 선언 제외)·자식을 옮긴다. 접두 자식
 * (`<svg:rect>`)은 부모의 기본 네임스페이스와 같아 XMLSerializer 가 접두 없이 낸다. 접두 없는 루트는 그대로.
 */
function unprefixRoot(doc) {
  const root = doc.documentElement;
  if (!root.prefix || root.namespaceURI !== SVG_NS || root.localName !== 'svg') return root;
  const fresh = doc.createElementNS(SVG_NS, 'svg');
  fresh.setAttributeNS(XMLNS_NS, 'xmlns', SVG_NS);
  for (const attr of Array.from(root.attributes)) {
    if (attr.namespaceURI !== XMLNS_NS) fresh.setAttributeNS(attr.namespaceURI, attr.name, attr.value);
  }
  while (root.firstChild) fresh.appendChild(root.firstChild);
  doc.replaceChild(fresh, root);
  return fresh;
}

function localName(el) {
  if (el.namespaceURI !== SVG_NS) throw new Rejected('forbidden_element', `SVG 네임스페이스가 아닌 요소 ${el.nodeName}`);
  return el.localName;
}

/**
 * root 와 모든 자손 요소(문서 순서). getElementsByTagName/children 을 안 쓰는 이유: jsdom 의 HTMLCollection 은
 * 색인 접근마다 namedItem 을 훑어 O(n²) 가 된다(4,000 요소에 5초). childNodes 는 양쪽(브라우저·jsdom) 다 O(1).
 */
function allElements(root) {
  const out = [];
  const stack = [root];
  while (stack.length) {
    const el = stack.pop();
    out.push(el);
    const kids = el.childNodes;
    for (let i = kids.length - 1; i >= 0; i -= 1) {
      if (kids[i].nodeType === ELEMENT_NODE) stack.push(kids[i]);
    }
  }
  return out;
}

/** 요소 자식만(childNodes 에서 고른다 — 위와 같은 이유로 .children 을 안 쓴다). */
function elementChildren(el) {
  return Array.from(el.childNodes).filter((n) => n.nodeType === ELEMENT_NODE);
}

/** 구조 검사 한 번에: 요소 허용목록·개수·깊이·배치·id·텍스트 자리·속성 이름. 값은 fixValues 에서. */
function walk(root, ids, drops, warnings, maxElements, maxDepth) {
  let count = 0;
  const stack = [[root, null, 1]];
  while (stack.length) {
    const [el, parent, depth] = stack.pop();
    const local = localName(el);
    const ptag = parent ? parent.localName : null;
    count += META.has(local) ? allElements(el).length : 1;   // 메타는 버리지만 자식 수는 상한에 넣는다
    if (count > maxElements) throw new Rejected('too_many_elements', `요소가 ${maxElements}개를 넘습니다`);
    if (depth > maxDepth) throw new Rejected('too_deep', `중첩이 ${maxDepth}단계를 넘습니다`);
    if (META.has(local)) { drops.push([el, 'meta']); continue; }
    if (parent && !INPUT_ELEMENTS.has(local)) throw new Rejected('forbidden_element', `허용하지 않는 요소 <${local}>`);   // 중첩 svg 도 여기서
    if (local === 'tspan' && ptag !== 'text' && ptag !== 'tspan') throw new Rejected('stray_text', '<tspan> 은 text/tspan 안에만 둘 수 있습니다');
    if (local === 'defs' && parent !== root) throw new Rejected('misplaced_defs', 'defs 는 <svg> 바로 아래에만 둘 수 있습니다');
    if (DEF_KINDS.has(local) && ptag !== 'defs') throw new Rejected('def_outside_defs', `<${local}> 는 defs 안에만 둘 수 있습니다`);
    if (local === 'style') {
      if (elementChildren(el).length || (ptag !== 'svg' && ptag !== 'defs')) throw new Rejected('unsupported_style', '지원하지 않는 <style> 구조입니다');
      drops.push([el, 'style']);
      continue;
    }
    checkAttrNames(el, el === root, local, ids, warnings);
    // 자식: 주석은 서버 파서가 못 보므로 조용히 제거. 글자는 text/tspan 안에서만(서버의 el.text / el.tail 검사).
    const children = [];
    let prev = null;
    for (const node of Array.from(el.childNodes)) {
      if (node.nodeType === COMMENT_NODE) node.remove();
      else if (node.nodeType === TEXT_NODE || node.nodeType === CDATA_NODE) {
        if (node.data.trim() && local !== 'text' && local !== 'tspan') {
          throw new Rejected('stray_text', prev ? `<${prev}> 뒤에 글자가 있습니다` : `<${local}> 안에 글자가 있습니다`);
        }
      } else if (node.nodeType === ELEMENT_NODE) {
        prev = node.localName;
        children.push(node);
      }
    }
    for (let i = children.length - 1; i >= 0; i -= 1) stack.push([children[i], el, depth + 1]);
  }
}

function checkAttrNames(el, isRoot, local, ids, warnings) {
  for (const attr of Array.from(el.attributes)) {
    const { name, value } = attr;
    const ns = attr.namespaceURI;
    if (ns === XMLNS_NS) {   // 네임스페이스 선언 — 서버 파서는 속성으로 안 본다. 루트의 xmlns="SVG NS" 만 남긴다
      if (!(isRoot && name === 'xmlns' && value === SVG_NS)) el.removeAttributeNode(attr);
      continue;
    }
    if (ns === XML_NS) {   // xml:space·xml:lang — 의미 없음, 제거
      el.removeAttributeNode(attr);
      warnings.push(`dropped_attr:xml:${attr.localName}`);
      continue;
    }
    if (ns !== null || /^on/i.test(name) || FORBIDDEN_ATTRS.has(name.toLowerCase())) {
      throw new Rejected('forbidden_attr', `허용하지 않는 속성 ${name}`);
    }
    if (name === 'style') continue;   // fixValues 에서 평탄화
    if (name === 'id') {
      if (ids.has(value)) throw new Rejected('duplicate_id', `id "${value}" 가 중복됩니다`);
      if (value.startsWith('svg-editor-')) throw new Rejected('reserved_id', `id "${value}" 는 편집기 예약 접두입니다`);
      ids.set(value, local);
    } else if (!ELEMENT_ATTRS.has(name) && !(isRoot && ROOT_ONLY_ATTRS.has(name))) {
      el.removeAttribute(name);
      warnings.push(`dropped_attr:${name}`);
    }
  }
}

/** CSS 주석 제거 — indexOf 로 한 번 훑는다(선형). 닫히지 않은 주석은 unsupported_style. */
function stripComments(css) {
  const out = [];
  let pos = 0;
  for (;;) {
    const start = css.indexOf('/*', pos);
    if (start < 0) break;
    const end = css.indexOf('*/', start + 2);
    if (end < 0) throw new Rejected('unsupported_style', '닫히지 않은 CSS 주석이 있습니다');
    out.push(css.slice(pos, start));
    pos = end + 2;
  }
  out.push(css.slice(pos));
  return out.join('');
}

/** 'name: value; …' → [[name, value]]. 꼴이 어긋나거나 !@{} 가 들어 있으면 unsupported_style, 참조형 이름은 forbidden_attr. */
function declarations(css) {
  const out = [];
  for (const part of stripComments(css).split(';')) {
    if (!part.trim()) continue;
    const sep = part.indexOf(':');
    const name = (sep < 0 ? part : part.slice(0, sep)).trim();
    const value = sep < 0 ? '' : part.slice(sep + 1).trim();
    if (sep < 0 || !value || !DECL_NAME.test(name) || /[!@{}]/.test(value)) {
      throw new Rejected('unsupported_style', `지원하지 않는 스타일 선언: ${part.trim().slice(0, 40)}`);
    }
    if (FORBIDDEN_ATTRS.has(name.toLowerCase())) throw new Rejected('forbidden_attr', `허용하지 않는 스타일 선언 ${name}`);
    out.push([name.toLowerCase(), value]);
  }
  return out;
}

/** class 이름 → 그 class 를 가진 노드 목록(문서 순서). <style> 평탄화가 규칙마다 트리를 돌지 않게. */
function classIndex(root) {
  const index = new Map();
  for (const node of allElements(root)) {
    for (const cls of (node.getAttribute('class') || '').split(/\s+/)) {
      if (!cls) continue;
      if (!index.has(cls)) index.set(cls, []);
      index.get(cls).push(node);
    }
  }
  return index;
}

/**
 * <style> 평탄화 — 시제품 flattenClassStyles 와 같은 규칙: 단일 .class 선택자·표현 속성 선언만, 뒤 규칙이 앞 규칙을
 * 덮는다. class 마다 선언을 먼저 합친 뒤(규칙 번호 보존) 노드마다 한 번만 적용 — 규칙 수 × 노드 수 곱을 피한다.
 */
function flattenSheet(index, sheet) {
  const css = stripComments(sheet.textContent || '').trim();
  const perClass = new Map();
  let pos = 0;
  let idx = 0;
  while (pos < css.length) {
    CSS_RULE.lastIndex = pos;
    const m = CSS_RULE.exec(css);
    const selectors = m ? m[1].split(',').map((s) => s.trim()) : [];
    if (!m || !selectors.every((s) => CSS_CLASS.test(s))) {
      throw new Rejected('unsupported_style', 'SVG CSS 는 단순 .class 선택자만 지원합니다');
    }
    const decls = declarations(m[2]);
    for (const [name] of decls) {
      if (!PRESENTATION_ATTRS.has(name)) throw new Rejected('unsupported_style', `지원하지 않는 CSS 속성 ${name}`);
    }
    for (const cls of new Set(selectors.map((s) => s.slice(1)))) {
      if (!perClass.has(cls)) perClass.set(cls, new Map());
      const merged = perClass.get(cls);
      for (const [name, value] of decls) merged.set(name, [idx, value]);
    }
    pos = m.index + m[0].length;
    idx += 1;
  }
  const applied = new Map();   // node → Map(name → 규칙 번호) — 여러 class 가 같은 이름을 줄 때 뒤 규칙이 이긴다
  for (const [cls, merged] of perClass) {
    for (const node of index.get(cls) || []) {
      if (!applied.has(node)) applied.set(node, new Map());
      const seen = applied.get(node);
      for (const [name, [ruleIdx, value]] of merged) {
        if (ruleIdx >= (seen.has(name) ? seen.get(name) : -1)) {
          seen.set(name, ruleIdx);
          node.setAttribute(name, value);
        }
      }
    }
  }
}

/** 값 검사·치환: style 속성 평탄화 → class 제거 → 위험 값·url() 참조 → 검정→currentColor → 글꼴 generic. */
function fixValues(el, ids, warnings) {
  const style = el.getAttribute('style');
  el.removeAttribute('style');
  for (const [name, value] of style ? declarations(style) : []) {
    if (PRESENTATION_ATTRS.has(name)) el.setAttribute(name, value);   // 인라인 style 이 표현 속성·class 규칙보다 우선
    else if (GEOMETRY_ATTRS.has(name)) throw new Rejected('unsupported_style', `style 안의 ${name} 은 받지 않습니다(기하는 속성으로)`);
    else warnings.push(`dropped_style:${name}`);
  }
  el.removeAttribute('class');
  for (const attr of Array.from(el.attributes)) {
    if (attr.namespaceURI === XMLNS_NS) continue;
    const { name, value } = attr;
    checkValue(name, value, ids);
    if (COLOR_ATTRS.has(name) && BLACK.has(value.replace(/\s/g, '').toLowerCase())) {
      el.setAttribute(name, 'currentColor');
    } else if (name === 'font-family') {
      const tokens = value.split(',').map((t) => t.trim().replace(/^['"]+|['"]+$/g, '').trim().toLowerCase());
      const generic = tokens.find((t) => GENERIC_FONTS.has(t));
      if (generic === undefined) {
        el.removeAttribute(name);
        warnings.push('font_family_dropped');
      } else {
        el.setAttribute(name, generic);
      }
    }
  }
}

function checkValue(name, value, ids) {
  const probe = value.replace(CONTROL, '');
  if (BAD_VALUE.test(probe)) throw new Rejected('bad_value', `${name} 값에 허용하지 않는 내용이 있습니다`);
  if (URL_FUNC.test(probe)) {
    const m = URL_REF.exec(probe.trim());
    const kinds = Object.prototype.hasOwnProperty.call(REF_KINDS, name) ? REF_KINDS[name] : null;
    if (!(m && kinds)) throw new Rejected('bad_value', `${name} 의 url() 은 같은 그림 안의 #id 참조만 허용합니다`);
    if (!kinds.includes(ids.get(m[1]))) throw new Rejected('broken_ref', `${name} 이 가리키는 #${m[1]} 이 없거나 종류가 다릅니다`);
  }
}

function num(s) {
  return typeof s === 'string' && NUMBER.test(s.trim()) ? Number(s.trim()) : null;
}

/** 루트 width/height 값: 숫자 또는 숫자+px → [단위 뗀 문자열, number]. 유한 양수가 아니면 null. */
function length(s) {
  let t = s.trim();
  if (t.toLowerCase().endsWith('px')) t = t.slice(0, -2).trimEnd();
  const n = num(t);
  return n !== null && Number.isFinite(n) && n > 0 ? [t, n] : null;
}

const stripZeros = (s) => (s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s);

/**
 * Python f'{n:g}' 와 같은 꼴 — 유효숫자 6, 뒤 0 제거. 반올림한 뒤의 지수가 -4 미만이거나 6 이상이면 지수 표기(가수 뒤 0 제거, 지수는
 * 부호 + 최소 두 자리: 1e-05 · 1.23457e+06), 아니면 고정 소수(0.0001 · 123457). JS toPrecision 은 지수를 -7 미만에서야 쓰므로
 * 1e-5 ~ 1e-7 구간이 서버와 달라져 직접 나눈다. 합성 viewBox 가 서버와 같은 문자열이 되게.
 */
function fmtG(n) {
  if (n === 0) return '0';
  const [mant, e] = n.toExponential(5).split('e');
  const exp = Number(e);
  if (exp < -4 || exp >= 6) return `${stripZeros(mant)}e${exp < 0 ? '-' : '+'}${String(Math.abs(exp)).padStart(2, '0')}`;
  return stripZeros(n.toPrecision(6));   // 이 구간에선 toPrecision 도 고정 소수 표기
}

/** 루트 width/height 정규화(숫자·px 만, 아니면 제거) → viewBox 검증(없으면 width/height 로 합성). [x, y, w, h] 반환. */
function rootBox(root, warnings) {
  const size = {};
  for (const name of ['width', 'height']) {
    if (!root.hasAttribute(name)) continue;
    const parsed = length(root.getAttribute(name));
    if (parsed === null) {
      root.removeAttribute(name);
      warnings.push(`dropped_attr:${name}`);
    } else {
      root.setAttribute(name, parsed[0]);
      size[name] = parsed[1];
    }
  }
  if (!root.hasAttribute('viewBox')) {
    if (!('width' in size) || !('height' in size)) throw new Rejected('bad_viewbox', 'viewBox 가 없습니다');
    root.setAttribute('viewBox', `0 0 ${fmtG(size.width)} ${fmtG(size.height)}`);
    warnings.push('viewbox_synthesized');
  }
  const vb = root.getAttribute('viewBox');
  const box = vb.replace(SURROUNDING_ASCII_WS, '').split(/[ \t\r\n,]+/).map(num);   // 구분자는 ASCII 만(브라우저와 같게)
  if (box.length !== 4 || box.some((v) => v === null || !Number.isFinite(v)) || box[2] <= 0 || box[3] <= 0) {
    throw new Rejected('bad_viewbox', `viewBox 가 올바르지 않습니다: ${vb.slice(0, 40)}`);
  }
  return box;
}

/**
 * 루트의 첫 그리기 자식(defs 제외)이 흰 rect(테두리·투명도·clip·transform 없음, viewBox 의 98% 이상)이고
 * 루트에 stroke 가 없으면 제거 — 다크모드에서 흰 판이 뜨지 않게. 겹쳐 둔 흰 판도 한 번에(재정제 멱등).
 */
function dropBackground(root, box, warnings) {
  while (isBackground(root, box)) {
    elementChildren(root).find((c) => c.localName !== 'defs').remove();
    warnings.push('dropped_background');
  }
}

function isBackground(root, box) {
  const el = elementChildren(root).find((c) => c.localName !== 'defs');
  if (!el || el.localName !== 'rect' || root.hasAttribute('stroke')) return false;
  if (['transform', 'opacity', 'fill-opacity', 'clip-path'].some((k) => el.hasAttribute(k))) return false;
  const fill = (el.getAttribute('fill') || '').replace(/\s/g, '').toLowerCase();
  const stroke = (el.getAttribute('stroke') ?? 'none').trim().toLowerCase();
  if (!WHITE.has(fill) || stroke !== 'none') return false;
  const [x0, y0, w, h] = box;
  const rx = num(el.getAttribute('x') ?? '0');
  const ry = num(el.getAttribute('y') ?? '0');
  const rw = num(el.getAttribute('width') ?? '');
  const rh = num(el.getAttribute('height') ?? '');
  if ([rx, ry, rw, rh].includes(null)) return false;
  const iw = Math.min(rx + rw, x0 + w) - Math.max(rx, x0);
  const ih = Math.min(ry + rh, y0 + h) - Math.max(ry, y0);
  return iw > 0 && ih > 0 && iw * ih >= 0.98 * w * h;
}
